import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { LifecycleBandView } from "@/types/dashboard/genie-console";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface LayoutProps {
  bands: LifecycleBandView[];
}

/**
 * Direction A — "Spine".
 *
 * A literal vertical lifecycle: a gradient rail runs down the left edge with a
 * lit node per stage, and each band's live content hangs off it in a
 * gradient-bordered card. Calm, document-like, reads top to bottom in one
 * pass. This is the default.
 */
export const SpineLayout = ({ bands }: LayoutProps) => (
  <div className="relative">
    {/* The spine itself — one continuous signature gradient behind the nodes. */}
    <div
      aria-hidden="true"
      className="absolute bottom-8 left-[19px] top-8 hidden w-[2px] rounded-full md:block"
      style={{
        background: `linear-gradient(180deg, ${bands[0]?.tint ?? "#4E9CFF"} 0%, ${
          bands[bands.length - 1]?.tint ?? "#FF4DB8"
        } 100%)`,
      }}
    />

    <div className="space-y-5">
      {bands.map((band) => (
        <section key={band.id} className="relative md:pl-16">
          {/* Node on the rail */}
          <div
            className="absolute left-0 top-6 hidden h-10 w-10 items-center justify-center rounded-full border-4 border-[#F9FAFC] md:flex"
            style={{ backgroundColor: `${band.tint}1a` }}
          >
            <GenieIcon name={band.genieIcon} size={18} color={band.tint} />
          </div>

          <div
            className="rounded-[20px] p-[1.5px] shadow-sm"
            style={{ background: `linear-gradient(135deg, ${band.tint}, ${band.tint}33)` }}
          >
            <div className="rounded-[18.5px] bg-white">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[#F3F4F6] px-5 py-4 md:px-6">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl md:hidden"
                    style={{ backgroundColor: `${band.tint}1a` }}
                  >
                    <GenieIcon name={band.genieIcon} size={17} color={band.tint} />
                  </span>
                  <div className="min-w-0">
                    <h2
                      className="text-[13px] font-extrabold tracking-[2px]"
                      style={{ color: band.tint }}
                    >
                      {band.label}
                    </h2>
                    <p className="truncate text-sm font-semibold text-[#222B45]">
                      {band.tagline}
                    </p>
                  </div>
                </div>
                <Link
                  to={band.linkTo}
                  className="inline-flex flex-shrink-0 items-center gap-1.5 text-sm font-semibold hover:underline"
                  style={{ color: band.tint }}
                >
                  {band.linkLabel}
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </Link>
              </div>

              <div className="px-5 py-4 md:px-6">
                <p className="mb-3 text-sm text-gray-600">{band.stateLine}</p>
                {band.preview}
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  </div>
);

export default SpineLayout;
